import {useState} from "react";
import Link from "next/link";
import {usePathname} from "next/navigation";
import Button from "@mui/material/Button";
import IconButton from "@mui/material/IconButton";
import Drawer from "@mui/material/Drawer";
import List from "@mui/material/List";
import ListItem from "@mui/material/ListItem";
import ListItemButton from "@mui/material/ListItemButton";
import ListItemText from "@mui/material/ListItemText";
import Divider from "@mui/material/Divider";
import MenuIcon from "@mui/icons-material/Menu";
import CloseIcon from "@mui/icons-material/Close";
import clsx from "clsx";
import styles from "./header.module.css";
import EmojiPeopleIcon from "@mui/icons-material/EmojiPeople";

type NavLink = { href: string; label: string; mobileOnly?: boolean };

const NAV_LINKS: NavLink[] = [
  {href: "/", label: "Start", mobileOnly: true},
  {href: "/press", label: "Presse"},
  {href: "/documentation", label: "Dokumentation"},
  {href: "/about", label: "Über Uns"},
  {href: "/contact", label: "Kontakt"},
  {href: "https://shop.orgvr.org", label: "Shop"}
];

export function Header() {
  const pathname = usePathname();
  const [open, setOpen] = useState(false);

  const toggle = (value: boolean) => () => setOpen(value);

  const isActive = (href: string) =>
      href === "/"
          ? pathname === "/"
          : pathname?.startsWith(href);

  return (
      <header className={clsx(styles.header)}>
        <div className={styles.left}>
          <Link href="/" className={styles.logoLink} aria-label="Startseite">
            <img src="/logo-small.svg" alt="Logo" width="90" height="45" className={styles.logo}/>
          </Link>

          {/* Desktop-Navigation */}
          <nav className={styles.desktopNav} aria-label="Hauptnavigation">
            {NAV_LINKS
                .filter(link => !link.mobileOnly)
                .map((l) => (
                    <Button
                        key={l.href}
                        component={Link}
                        href={l.href}
                        variant="text"
                        className={clsx(styles.navBtn, isActive(l.href) && styles.navBtnActive)}
                    >
                      {l.label}
                    </Button>
                ))}
          </nav>
        </div>

        <div className={styles.right}>
          {/* Desktop: Login */}
          <nav className={styles.desktopNav} aria-label="Akademie">
            <Button
                component={Link}
                href="https://iov-akademie.org/"
                variant="contained"
                className={styles.loginBtn}
                sx={{"backgroundColor": "#b88e23"}}
            >
              Akademie
            </Button>
          </nav>
          <nav className={styles.desktopNav} aria-label="Benutzer">
            <Button
                component={Link}
                href="https://iov-organisation.org/zivilist-werden/"
                startIcon={<EmojiPeopleIcon/>}
                variant="contained"
                className={styles.loginBtn}
            >
              Zivilist werden
            </Button>
          </nav>

          {/* Mobile: Hamburger */}
          <IconButton
              aria-label="Menü öffnen"
              className={styles.mobileMenuButton}
              onClick={toggle(true)}
              size="large"
          >
            <MenuIcon/>
          </IconButton>
        </div>

        {/* Mobile Drawer */}
        <Drawer anchor="right" open={open} onClose={toggle(false)}>
          <div className={styles.drawer}>
            <div className={styles.drawerHeader}>
              <span className={styles.drawerTitle}>Menü</span>
              <IconButton aria-label="Menü schließen" onClick={toggle(false)}>
                <CloseIcon/>
              </IconButton>
            </div>
            <Divider/>

            <List aria-label="Mobiles Hauptmenü">
              {NAV_LINKS.map((l) => (
                  <ListItem key={l.href} disablePadding>
                    <ListItemButton
                        component={Link}
                        href={l.href}
                        onClick={toggle(false)}
                        selected={isActive(l.href)}
                    >
                      <ListItemText primary={l.label}/>
                    </ListItemButton>
                  </ListItem>
              ))}
            </List>

            <Divider className={styles.drawerDivider}/>

            <div className={styles.drawerFooter}>
              <Button
                  component={Link}
                  href="https://iov-akademie.org/"
                  variant="contained"
                  sx={{"backgroundColor": "#b88e23"}}
                  fullWidth
              >
                Akademie
              </Button>
              <Button
                  component={Link}
                  href="https://iov-organisation.org/zivilist-werden/"
                  startIcon={<EmojiPeopleIcon/>}
                  variant="contained"
                  fullWidth
                  sx={{mt: 2}}
              >
                Zivilist werden
              </Button>
            </div>
          </div>
        </Drawer>
      </header>
  );
}

export default Header;
